import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {usePaymentIntents} from '../hooks/usePaymentIntents';
import {usePaymentIntentsFilter} from '../hooks/usePaymentIntentsFilter';
import {usePaymentIntentsSearch} from '../hooks/usePaymentIntentsSearch';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {PaymentIntent} from '../types';
import {UsePaymentIntentsSelectionResult} from '../hooks/usePaymentIntentsSelection';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + RowSelectionDataCell + the app-local PaginationControls) to the
// shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows and supported
// selection — but it structurally could not have sorting, token pagination, or
// the loading/empty/error states, because all three come from the shared
// component. Five checklist gaps close by changing this one component (§3z).
//
// ⚠️ NOTE FOR THE REMAINING MODULES: `src/components/PaginationControls` and
// `src/hooks/usePagination` are NOT deleted here. Other modules still import
// them; they come out in a later wave once every module is migrated.
//
// ═══ MONEY PATH: THE INLINE EDIT DRAWER IS GONE, DELIBERATELY ═══
//
// The old table opened an inline FormDrawer on row click and submitted through
// `update()` — a mutation on a live payment intent, fired from a list row, with
// a payload built by DELETING three fields off the Formik object
// (`delete payload.created` / `automaticPaymentMethods` / `clientSecret`).
// That is a denylist on a money mutation: any field the schema later stops
// accepting, or any field Formik picks up, rides along silently. It is also
// FNP-996's exact shape — the object being mutated was the caller's.
//
// Editing a payment intent is a money-path flow that belongs on the EditPage,
// which already exists and already has the form. Rows are therefore INERT here
// rather than being re-plumbed into a flow this pass is not allowed to change.
// The drawer components are untouched and still used by AddRecordButton and
// EditPage. The allowlist fix for `update()` itself is in
// services/PaymentIntentsService.ts.

interface RecordsTableProps {
    /**
     * Selection is read by IndexPage and passed down, because the delete
     * button lives in the page HEADER — a sibling, not a child of this table.
     * The state itself is the Redux payment-intents.selection slice (FNP-951);
     * taking it as a prop keeps this component store-free and testable.
     */
    selection?: UsePaymentIntentsSelectionResult;
    /**
     * The customer whose payment intents to list.
     *
     * ⚠️ OPTIONAL, and genuinely so: `PaymentIntentsFilterInput.customerId` is
     * nullable, so the index page lists every payment intent while
     * customers/pages/EditPage renders `<PaymentIntentsTable customerId={id}/>`
     * inside a tab to scope it. Unlike payment-methods there is no route param
     * fallback — this table is never mounted on a customer route of its own.
     */
    customerId?: string;
}

export const RecordsTable = ({
                                 selection,
                                 customerId,
                             }: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions();

    const {filters} = usePaymentIntentsFilter();

    const {
        columns,
        // The loaded window BEFORE filtering — the denominator the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = usePaymentIntents(customerId, filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = usePaymentIntentsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The select column is HIDDEN, not disabled, when the action is not
    // permitted (§2, §10). Gated on the same action the delete button is gated
    // on — a checkbox with no permitted batch action behind it is an affordance
    // that does nothing.
    const canSelect = !!selection
        && allowedActions.includes(rbacActions.DeletePaymentIntent);

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection.selection[item.id])
        : undefined;

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both: "no payment intents" sends an
    // operator hunting for a bug when a search simply matched nothing.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No payment intents match this search')
        : I18n.get('No payment intents found');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown, which token pagination cannot honour — there is no page
              * to resize and no total to divide (§9). The previous table passed
              * `limit={10}` and a no-op handler to satisfy it, so the dropdown
              * rendered and did nothing.
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load payment intents')}
                >
                    {I18n.get(
                        'The billing API did not return the payment intent list. Try Reload; '
                        + 'if it persists the API may be unavailable in this environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<PaymentIntent>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the
                // same scope the keyword filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records: PaymentIntent[]) =>
                        selection.handleSelectAllRows(true, records)
                    : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              * A SIBLING of the table, not a table prop: the table renders rows,
              * the footer answers "have I seen everything?" (§9).
              *
              * `loadedCount` is the rows in the browser — NEVER a dataset total.
              * `filterTerm` must be passed whenever a keyword is active, or a
              * filtered view silently reports the unfiltered state.
              */}
            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'payment intent'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
